import React from "react";
import { Box, Button, TextField } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Modal } from "components/Modal";
import { normalizeImportedUserRow } from "utils/email";

type StudentFormData = {
  name: string;
  email: string;
};

type AddStudentModalProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (student: StudentFormData) => void;
};

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email address is required"),
});

const initialValues: StudentFormData = {
  name: "",
  email: "",
};

export const AddStudentModal: React.FC<AddStudentModalProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      onAdd(normalizeImportedUserRow(values));
      resetForm();
      onClose();
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleCancel} title="Add Student" size="small">
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mt: 2 }}>
            <TextField
              variant="outlined"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              margin="dense"
              required
              fullWidth
              name="name"
              label="Name"
              type="text"
              id="name"
              size="small"
              value={formik.values.name}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            <TextField
              variant="outlined"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              margin="dense"
              required
              fullWidth
              id="email"
              size="small"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formik.values.email}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Box>

          <Box
            sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button variant="outlined" onClick={handleCancel} type="button">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!formik.isValid || formik.isSubmitting}
            >
              Add
            </Button>
          </Box>
        </form>
      </FormikProvider>
    </Modal>
  );
};
